import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { MF_PENDING_TICKET_MESSAGE_SCROLL_KEY } from "../../constants/pendingTicketMessageScroll";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import canTransferTicket from "../../helpers/canTransferTicket";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add, PlayCircleFilled as PlayIcon } from "@material-ui/icons";
import PushPinIcon from "@mui/icons-material/PushPin";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { useModernLayout } from "../../context/ModernLayout/ModernLayoutContext";
import NewTicketModal from "../NewTicketModal";
import { resolveTicketContactAvatar } from "../../utils/avatar";
import { resumeTicket } from "../../services/ticketPauseService";
import confirmDialog from "../../utils/confirmDialog";
import {
  Avatar,
  Badge,
  Box,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MarkunreadIcon from "@material-ui/icons/Markunread";
import DraftsIcon from "@material-ui/icons/Drafts";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import TicketTagPickerModal from "../TicketTagPickerModal";
import KanbanEnterpriseMoveModal, {
  useKanbanEnterpriseEnabled,
} from "../KanbanEnterpriseMoveModal";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    transition: "background-color 0.2s ease",
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "dark" 
        ? "rgba(25, 118, 210, 0.2)" 
        : "rgba(25, 118, 210, 0.15)",
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      "&:hover": {
        backgroundColor: theme.palette.type === "dark" 
          ? "rgba(25, 118, 210, 0.25)" 
          : "rgba(25, 118, 210, 0.2)",
      },
    },
  },

  // Borda arredondada e separador entre tickets (Layout Moderno e Clássico)
  ticketModernMobile: {
    borderRadius: 12,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    "&:last-child": {
      marginBottom: 0,
    },
  },

  pendingTicket: {
    cursor: "unset",
  },
  pausedTicket: {
    borderLeft: "4px solid #FF9800 !important",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.5em",
    whiteSpace: "nowrap",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: 0,
    color: "green",
    fontWeight: "bold",
    marginRight: "10px",
    borderRadius: 0,
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 6,
    marginLeft: "5px",
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : "white",
  },

  lastMessageTime: {
    flexShrink: 0,
    textAlign: "right",
    whiteSpace: "nowrap",
    fontSize: "0.8rem",
    color: theme.mode === "light" ? "black" : grey[400],
  },

  lastMessageTimeUnread: {
    flexShrink: 0,
    textAlign: "right",
    whiteSpace: "nowrap",
    fontSize: "0.8rem",
    color: "green",
    fontWeight: "bold",
  },

  // Fonte da data em verde: apenas no Layout Moderno + Mobile
  lastMessageTimeFontModernMobile: {
    fontFamily: "Roboto, Arial, sans-serif",
    fontSize: "0.8125rem",
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "0%",
    marginLeft: "5px",
    color: theme.mode === "light" ? "black" : grey[400],
  },

  contactLastMessageUnread: {
    paddingRight: 20,
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : grey[400],
    // width: "50%",
  },

  badgeStyle: {
    color: "white",
    backgroundColor: green[500],
  },

  // Bolinha de não lidas em cima da foto do contato (canto superior esquerdo)
  unreadBadgeOnAvatar: {
    "& .MuiBadge-badge": {
      top: -1,
      left: 45,
      transform: "scale(1) translate(-50%, -50%)",
    },
  },

  acceptButton: {
    position: "absolute",
    right: "1px",
  },

  ticketQueueColor: {
    flex: "none",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
  },

  ticketInfo: {
    position: "relative",
    top: -13,
  },
  secondaryContentSecond: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    flexDirection: "row",
    alignContent: "flex-start",
    gap: "2px",
  },
  kanbanTagsRow: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    alignItems: "center",
    gap: "2px",
    marginTop: 2,
    marginLeft: 5,
    width: "100%",
    paddingRight: 72,
    boxSizing: "border-box",
  },
  kanbanTag: {
    color: "#FFF",
    border: "1px solid #CCC",
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: "0.6em",
    fontWeight: "bold",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  kanbanEnterpriseDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    display: "inline-block",
    border: "1px solid rgba(0,0,0,0.18)",
    boxSizing: "border-box",
    cursor: "default",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0,
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3,
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  connectionIcon: {
    marginRight: theme.spacing(1),
  },

  // Estilos para o modal da imagem
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },
  campaignProIcon: {
    position: "absolute",
    left: -2,
    top: -2,
    width: 18,
    height: 18,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "50%",
    padding: 2,
    zIndex: 1,
  },
  ticketMenuButton: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 2,
    padding: 4,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(30,30,30,0.85)"
        : "rgba(255,255,255,0.9)",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(50,50,50,0.95)"
          : "rgba(245,245,245,1)",
    },
  },
  selectionCheckbox: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 3,
    padding: 0,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(30,30,30,0.85)"
        : "rgba(255,255,255,0.9)",
    borderRadius: 4,
  },
}));

const TicketListItemCustom = ({
  setTabOpen,
  ticket,
  selected = false,
  isPinned,
  onTogglePin,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  onEnterSelectionMode,
  onTicketUnreadChange,
  onTicketDeleted,
  onTicketStatusChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isModernLayout = useModernLayout();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [tagsOverflowModalOpen, setTagsOverflowModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [kanbanPickerOpen, setKanbanPickerOpen] = useState(false);
  const [kanbanEnterprisePickerOpen, setKanbanEnterprisePickerOpen] = useState(false);
  const showKanbanEnterprise = useKanbanEnterpriseEnabled(ticket);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem
  const [elapsedTime, setElapsedTime] = useState("");

  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Timer incremental - mostra tempo decorrido desde ultima atualizacao
  useEffect(() => {
    const calcElapsed = () => {
      if (!ticket?.updatedAt) return "";
      const updated = parseISO(ticket.updatedAt);
      const now = new Date();
      const diffMs = now - updated;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHrs = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays > 0) return `${diffDays}d ${diffHrs % 24}h`;
      if (diffHrs > 0) return `${diffHrs}h ${diffMin % 60}m`;
      return `${diffMin}m`;
    };
    setElapsedTime(calcElapsed());
    const interval = setInterval(() => {
      if (isMounted.current) setElapsedTime(calcElapsed());
    }, 60000); // atualiza a cada 1 minuto
    return () => clearInterval(interval);
  }, [ticket?.updatedAt]);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenTicketMenu = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleCloseTicketMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuPin = () => {
    handleCloseTicketMenu();
    if (onTogglePin) onTogglePin(ticket.id);
  };

  const handleMenuToggleRead = async () => {
    handleCloseTicketMenu();
    const isUnread = Number(ticket.unreadMessages) > 0;
    const unreadMessages = isUnread ? 0 : 1;
    try {
      await api.put(`/tickets/${ticket.id}`, { unreadMessages });
      if (onTicketUnreadChange) {
        onTicketUnreadChange(ticket.id, unreadMessages);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleMenuSelectMode = () => {
    handleCloseTicketMenu();
    if (onEnterSelectionMode) onEnterSelectionMode();
    if (onToggleSelect) onToggleSelect(ticket.id);
  };

  const handleAddTag = async (tagId) => {
    const contactId = ticket.contactId || ticket.contact?.id;
    if (!contactId) return;
    await api.post("/contacts/bulk-tags", {
      contactIds: [contactId],
      tags: [{ id: tagId }],
    });
    toast.success("Tag adicionada com sucesso");
  };

  const handleKanbanMove = async (tagId) => {
    if (ticket.tags && ticket.tags.length > 0) {
      await api.delete(`/ticket-tags/${ticket.id}`);
    }
    await api.put(`/ticket-tags/${ticket.id}/${tagId}`);
    toast.success("Etapa do Kanban atualizada");
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Verificar se exigir motivo de finalização está ativo (config empresa)
    let exigirMotivo = false;
    try {
      const s = await getSetting({ column: "exigirMotivoFinalizacao" });
      exigirMotivo = s?.exigirMotivoFinalizacao === true || s?.exigirMotivoFinalizacao === "true";
    } catch (_) {}

    if (exigirMotivo) {
      // Abrir o modal de finalização
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
              queueId: ticket?.queue?.id || ticket?.queueId || null, // ✅ Preservar fila ao fechar
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        if (typeof onTicketDeleted === "function") {
          onTicketDeleted(id);
        }
        if (typeof onTicketStatusChange === "function") {
          onTicketStatusChange();
        }
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
            queueId: ticket?.queue?.id || ticket?.queueId || null, // ✅ Preservar fila ao fechar
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    // Remoção otimista IMEDIATA da UI
    if (typeof onTicketDeleted === "function") {
      onTicketDeleted(id);
    }
    if (typeof onTicketStatusChange === "function") {
      onTicketStatusChange();
    }
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        queueId: ticket?.queue?.id || ticket?.queueId || null, // ✅ Preservar fila ao fechar
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
      toast.success(i18n.t("ticketsList.buttons.ignore") || "Ticket ignorado");
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    // Remoção otimista IMEDIATA da UI para tickets pendentes
    if (ticket.status === "pending") {
      if (typeof onTicketDeleted === "function") {
        onTicketDeleted(id);
      }
      if (typeof onTicketStatusChange === "function") {
        onTicketStatusChange();
      }
    }
    setLoading(true);
    try {
      // Buscar ticket fresh + config de greeting EM PARALELO
      const [freshRes, settingRes] = await Promise.all([
        api.get(`/tickets/${id}`),
        getSetting({ column: "sendGreetingAccepted" }).catch(() => null),
      ]);

      const freshTicket = freshRes.data;

      // Verificar se ticket já foi aceito por outro atendente
      if (freshTicket && freshTicket.status === "open" && freshTicket.userId && freshTicket.userId !== user?.id) {
        setLoading(false);
        setOpenAlert(true);
        setUserTicketOpen(freshTicket.user?.name || `Atendente #${freshTicket.userId}`);
        setQueueTicketOpen(freshTicket.queue?.name || "");
        return;
      }

      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        // Navegar IMEDIATAMENTE para o ticket
        if (isMounted.current) setLoading(false);
        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(otherTicket.data || ticket);
        history.push(`/tickets/${otherTicket.data?.uuid || ticket.uuid}`);

        // Greeting em background (não bloqueia navegação)
        const greetingMode = settingRes?.sendGreetingAccepted || "disabled";
        const canGreet = !ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled";

        if (canGreet && greetingMode === "enabled") {
          handleSendMessage(ticket.id);
        } else if (canGreet && greetingMode === "ask") {
          const confirmed = await confirmDialog({
            title: i18n.t("settings.settings.options.sendGreetingAccepted"),
            message: i18n.t("settings.settings.options.askGreetingConfirm"),
          });
          if (confirmed) {
            handleSendMessage(ticket.id);
          }
        }
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleResumeTicket = async (id) => {
    setLoading(true);
    try {
      await resumeTicket(id);
      toast.success("Ticket retomado!");
      setTabOpen("open");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    if (
      ticket?.searchMatchMessageId != null &&
      ticket?.id != null
    ) {
      try {
        sessionStorage.setItem(
          MF_PENDING_TICKET_MESSAGE_SCROLL_KEY,
          JSON.stringify({
            messageId: ticket.searchMatchMessageId,
            ticketDbId: Number(ticket.id),
          })
        );
      } catch (_) {
        /* ignore */
      }
    }
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      const isLocationMessage = ticket.lastMessage.includes("maps.google.com/maps?q=");
      return (
        <MarkdownWrapper>
          {isLocationMessage ? "Localização" : "Anúncio"}
        </MarkdownWrapper>
      );
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    // ✅ CORREÇÃO: Extrair reação da última mensagem se houver
    // Reações são emojis únicos no final da mensagem (após espaço)
    // Verificar se a mensagem termina com um espaço seguido de um emoji
    const lastChar = ticket.lastMessage.slice(-1);
    const secondLastChar = ticket.lastMessage.slice(-2, -1);
    // Se o penúltimo caractere é espaço e o último é um emoji (verificar se é emoji Unicode)
    const isEmoji = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(lastChar);
    const reaction = (secondLastChar === ' ' && isEmoji) ? lastChar : null;
    const messageText = reaction ? ticket.lastMessage.slice(0, -2).trim() : ticket.lastMessage;

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <MarkdownWrapper>
          {truncate(messageText, 40)}
        </MarkdownWrapper>
        {reaction && (
          <span style={{ fontSize: '1.1em', flexShrink: 0 }}>{reaction}</span>
        )}
      </span>
    );
  };

  const visibleTagsLimit = 4;
  const contactTags = Array.isArray(ticket?.contact?.tags) ? ticket.contact.tags : [];
  const ticketTags = Array.isArray(ticket?.tags) ? ticket.tags : [];
  const visibleContactTags = contactTags.slice(0, visibleTagsLimit);
  const hiddenContactTags = contactTags.slice(visibleTagsLimit);

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {canTransferTicket(user) && transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <TicketTagPickerModal
        open={tagPickerOpen}
        onClose={() => setTagPickerOpen(false)}
        kanban={0}
        title="Adicionar tag"
        confirmLabel="Adicionar"
        onSelect={handleAddTag}
      />
      <TicketTagPickerModal
        open={kanbanPickerOpen}
        onClose={() => setKanbanPickerOpen(false)}
        kanban={1}
        title="Mover para etapa do Kanban"
        confirmLabel="Mover"
        onSelect={handleKanbanMove}
      />
      <KanbanEnterpriseMoveModal
        open={kanbanEnterprisePickerOpen}
        onClose={() => setKanbanEnterprisePickerOpen(false)}
        ticket={ticket}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseTicketMenu}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {(ticket.status === "open" || ticket.status === "group") && (
          <MenuItem onClick={handleMenuPin}>
            <PushPinIcon fontSize="small" style={{ marginRight: 12, transform: "rotate(45deg)" }} />
            {isPinned ? "Desafixar conversa" : "Fixar conversa"}
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuToggleRead}>
          {Number(ticket.unreadMessages) > 0 ? (
            <DraftsIcon fontSize="small" style={{ marginRight: 12 }} />
          ) : (
            <MarkunreadIcon fontSize="small" style={{ marginRight: 12 }} />
          )}
          {Number(ticket.unreadMessages) > 0
            ? "Marcar como lida"
            : "Marcar como não lida"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseTicketMenu();
            setTagPickerOpen(true);
          }}
        >
          <LocalOfferOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
          Adicionar tag
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseTicketMenu();
            setKanbanPickerOpen(true);
          }}
        >
          <ViewColumnOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
          Kanban
        </MenuItem>
        {showKanbanEnterprise && (
          <MenuItem
            onClick={() => {
              handleCloseTicketMenu();
              setKanbanEnterprisePickerOpen(true);
            }}
          >
            <DashboardOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
            Kanban Enterprise
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuSelectMode}>
          <CheckBoxOutlineBlankIcon fontSize="small" style={{ marginRight: 12 }} />
          Selecionar
        </MenuItem>
      </Menu>
      <ListItem
        button
        dense
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          if (selectionMode) {
            if (onToggleSelect) onToggleSelect(ticket.id);
            return;
          }

          handleSelectTicket(ticket);
        }}
        selected={!selectionMode && selected}
        className={clsx(classes.ticket, classes.ticketModernMobile, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.pausedTicket]: ticket.status === "paused",
        })}
      >
        {selectionMode && (
          <Checkbox
            className={classes.selectionCheckbox}
            color="primary"
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              if (onToggleSelect) onToggleSelect(ticket.id);
            }}
          />
        )}
        {!selectionMode && (
          <IconButton
            className={classes.ticketMenuButton}
            size="small"
            onClick={handleOpenTicketMenu}
            aria-label="Opções do ticket"
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
        <ListItemAvatar style={{ marginLeft: "-15px", position: "relative" }}>
          <Box component="span" style={{ position: "relative", display: "block" }}>
            {ticket.campaignProId && (
              <Tooltip arrow placement="top" title="Ticket da Campanha PRO">
                <span className={classes.campaignProIcon}>
                  <AnnouncementIcon style={{ fontSize: 14 }} />
                </span>
              </Tooltip>
            )}
            {ticket.unreadMessages > 0 ? (
              <Badge
                badgeContent={shouldBlurMessages ? "?" : ticket.unreadMessages}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                classes={{ badge: classes.badgeStyle }}
                className={classes.unreadBadgeOnAvatar}
              >
                <Avatar
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                  }}
                  src={resolveTicketContactAvatar(ticket)}
                  className={classes.clickableAvatar}
                  onClick={handleImageClick}
                />
              </Badge>
            ) : (
              <Avatar
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                }}
                src={resolveTicketContactAvatar(ticket)}
                className={classes.clickableAvatar}
                onClick={handleImageClick}
              />
            )}
          </Box>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
              >
                {ticket.isGroup && ticket.channel === "whatsapp" && (
                  <GroupIcon
                    fontSize="small"
                    style={{
                      color: grey[700],
                      marginBottom: "-1px",
                      marginLeft: "5px",
                    }}
                  />
                )}{" "}
                &nbsp;
                {ticket.channel && (
                  <ConnectionIcon
                    width="20"
                    height="20"
                    className={classes.connectionIcon}
                    connectionType={ticket.channel}
                  />
                )}{" "}
                &nbsp;
                {truncate(ticket.contact?.name, 60)}
              </Typography>
              {ticket.lastMessage && (
                <Tooltip title={format(parseISO(ticket.updatedAt), "dd/MM/yyyy HH:mm")}>
                  <Typography
                    className={clsx(
                      Number(ticket.unreadMessages) > 0
                        ? classes.lastMessageTimeUnread
                        : classes.lastMessageTime,
                      isModernLayout && isMobile && classes.lastMessageTimeFontModernMobile
                    )}
                    component="span"
                    variant="body2"
                  >
                    {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                      <>{format(parseISO(ticket.updatedAt), "HH:mm")} ({elapsedTime})</>
                    ) : (
                      <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")} ({elapsedTime})</>
                    )}
                  </Typography>
                </Tooltip>
              )}
            </span>
          }
          secondary={
            <span className={classes.contactNameWrapper}>
              <Typography
                className={
                  Number(ticket.unreadMessages) > 0
                    ? classes.contactLastMessageUnread
                    : classes.contactLastMessage
                }
                noWrap
                component="span"
                variant="body2"
              >
                {renderLastMessage()}
                <span className={classes.secondaryContentSecond}>
                  {ticket?.whatsapp ? (
                    <Badge
                      className={classes.connectionTag}
                      style={{
                        backgroundColor: (() => {
                          const configured =
                            ticket.whatsapp?.color &&
                            String(ticket.whatsapp.color).trim();
                          if (configured) return ticket.whatsapp.color;
                          if (ticket.channel === "facebook") return "#4267B2";
                          if (ticket.channel === "instagram") return "#E1306C";
                          if (ticket.channel === "telegram") return "#229ED9";
                          if (ticket.channel === "webchat") return "#0066ff";
                          return "#25D366";
                        })(),
                      }}
                    >
                      {ticket.whatsapp?.name.toUpperCase()}
                    </Badge>
                  ) : (
                    <br></br>
                  )}
                  {
                    <Badge
                      style={{
                        backgroundColor: ticket.queue?.color || "#7c7c7c",
                      }}
                      className={classes.connectionTag}
                    >
                      {ticket.queueId
                        ? ticket.queue?.name.toUpperCase()
                        : ticket.status === "lgpd"
                        ? "LGPD"
                        : `${i18n.t("momentsUser.noqueue")}`}
                    </Badge>
                  }
                  {ticket?.user && (
                    <Badge
                      style={{ backgroundColor: "#000000" }}
                      className={classes.connectionTag}
                    >
                      {ticket.user?.name.toUpperCase()}
                    </Badge>
                  )}
                </span>
                <span className={classes.secondaryContentSecond}>
                  {visibleContactTags.map((tag, idx) => (
                    <ContactTag
                      tag={tag}
                      key={`ticket-contact-tag-${ticket.id}-${tag.id || idx}`}
                    />
                  ))}
                  {hiddenContactTags.length > 0 && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setTagsOverflowModalOpen(true);
                      }}
                      style={{
                        padding: "1px 6px",
                        borderRadius: "3px",
                        fontSize: "0.65em",
                        fontWeight: "bold",
                        color: "#666",
                        marginRight: "4px",
                        marginTop: "2px",
                        whiteSpace: "nowrap",
                        background: "#e0e0e0",
                        cursor: "pointer",
                      }}
                      title="Ver todas as tags"
                    >
                      +{hiddenContactTags.length}...
                    </div>
                  )}
                </span>
                {(ticketTags.length > 0 || ticket?.kanbanColumn) && (
                  <span className={classes.kanbanTagsRow}>
                    {ticket?.kanbanColumn && (
                      <span
                        className={classes.kanbanEnterpriseDot}
                        title={ticket.kanbanColumn.name}
                        style={{
                          backgroundColor:
                            ticket.kanbanColumn.color || "#7C7C7C",
                        }}
                      />
                    )}
                    {ticketTags.map((tag, idx) => (
                      <div
                        key={`ticket-kanban-tag-${ticket.id}-${tag.id || idx}`}
                        className={classes.kanbanTag}
                        title={tag.name}
                        style={{ backgroundColor: tag.color || "#7C7C7C" }}
                      >
                        {tag.name}
                      </div>
                    ))}
                  </span>
                )}
              </Typography>
            </span>
          }
        />
        <ListItemSecondaryAction>
          {/* Para tickets com status chatbot, mostrar apenas o ícone de spy */}
          {ticket.status === "chatbot" && (
            <span className={classes.secondaryContentSecond}>
              <ButtonWithSpinner
                style={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                  color: theme.mode === "light" ? "blue" : "#FFF",
                  padding: "0px",
                  borderRadius: "50%",
                  right: "1px",
                  fontSize: "0.6rem",
                  bottom: "-30px",
                  minWidth: "2em",
                  width: "auto",
                }}
                variant="contained"
                className={classes.acceptButton}
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpyTicket();
                }}
              >
                <Tooltip title="Espiar conversa do chatbot">
                  <VisibilityIcon />
                </Tooltip>
              </ButtonWithSpinner>
            </span>
          )}

          {/* Para todos os outros status, manter os botões originais */}
          {ticket.status !== "chatbot" && (
            <>
              {/* Botão fixar/desfixar — acima do fechar */}
              {(ticket.status === "open" || ticket.status === "group") && (
                <span className={classes.secondaryContentSecond}>
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: isPinned ? "#1976d2" : "#999",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "1px",
                      position: "absolute",
                      fontSize: "0.6rem",
                      bottom: "-14px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onTogglePin) onTogglePin(ticket.id);
                    }}
                  >
                    <Tooltip title={isPinned ? "Desfixar conversa" : "Fixar conversa"}>
                      <PushPinIcon style={{ fontSize: 24, transform: "rotate(45deg)" }} />
                    </Tooltip>
                  </ButtonWithSpinner>
                </span>
              )}
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "pending" &&
                  (ticket.queueId === null || ticket.queueId === undefined) && (
                    <ButtonWithSpinner
                      style={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        color: theme.mode === "light" ? "green" : "#FFF",
                        padding: "0px",
                        borderRadius: "50%",
                        right: "51px",
                        fontSize: "0.6rem",
                        bottom: "-40px",
                        minWidth: "2em",
                        width: "auto",
                      }}
                      variant="contained"
                      className={classes.acceptButton}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                        <Done />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "pending" && ticket.queueId !== null && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.mode === "light" ? "green" : "#FFF",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "51px",
                      fontSize: "0.6rem",
                      bottom: "-40px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleAcepptTicket(ticket.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Done />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond1}>
                {canTransferTicket(user) &&
                  (ticket.status === "pending" ||
                    ticket.status === "open" ||
                    ticket.status === "group") && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.mode === "light" ? "purple" : "#FFF",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "26px",
                      position: "absolute",
                      fontSize: "0.6rem",
                      bottom: "-40px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={handleOpenTransferModal}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                      <SwapHoriz />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {(ticket.status === "open" || ticket.status === "group") && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.mode === "light" ? "red" : "#FFF",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "1px",
                      fontSize: "0.6rem",
                      bottom: "-40px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleCloseTicket(ticket.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                      <HighlightOff />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {(ticket.status === "pending" || ticket.status === "lgpd") &&
                  (user.userClosePendingTicket === "enabled" ||
                    user.profile === "admin") && (
                    <ButtonWithSpinner
                      style={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        color: theme.mode === "light" ? "red" : "#FFF",
                        padding: "0px",
                        borderRadius: "50%",
                        right: "1px",
                        fontSize: "0.6rem",
                        bottom: "-40px",
                        minWidth: "2em",
                        width: "auto",
                      }}
                      variant="contained"
                      className={classes.acceptButton}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                        <HighlightOff />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "closed" && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.mode === "light" ? "green" : "#FFF",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "1px",
                      fontSize: "0.6rem",
                      bottom: "-30px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNewTicketModal();
                    }}
                  >
                    <Tooltip title="Criar Novo Ticket">
                      <Add />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "paused" && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: "#4CAF50",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "1px",
                      fontSize: "0.6rem",
                      bottom: "-40px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeTicket(ticket.id);
                    }}
                  >
                    <Tooltip title="Retomar atendimento">
                      <PlayIcon />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
            </>
          )}
        </ListItemSecondaryAction>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      <Dialog
        open={tagsOverflowModalOpen}
        onClose={() => setTagsOverflowModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tags ({contactTags.length})</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {contactTags.map((tag, idx) => (
              <ContactTag
                tag={tag}
                key={`ticket-overflow-tag-${ticket.id}-${tag.id || idx}`}
              />
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagsOverflowModalOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img 
            src={resolveTicketContactAvatar(ticket)} 
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.selected !== nextProps.selected) return false;
  if (prevProps.isPinned !== nextProps.isPinned) return false;
  if (prevProps.selectionMode !== nextProps.selectionMode) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.ticket?.id !== nextProps.ticket?.id) return false;
  if (prevProps.ticket?.updatedAt !== nextProps.ticket?.updatedAt) return false;
  if (prevProps.ticket?.unreadMessages !== nextProps.ticket?.unreadMessages) return false;
  if (prevProps.ticket?.status !== nextProps.ticket?.status) return false;
  if (prevProps.ticket?.userId !== nextProps.ticket?.userId) return false;
  if (prevProps.ticket?.queueId !== nextProps.ticket?.queueId) return false;
  if (prevProps.ticket?.lastMessage !== nextProps.ticket?.lastMessage) return false;
  return true;
};

export default React.memo(TicketListItemCustom, arePropsEqual);